import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleAlert,
  CircleCheck,
  Mail,
  MapPin,
  Phone,
  Store,
  UserRound,
  Volleyball,
  WandSparkles,
  type LucideIcon,
} from 'lucide-react';
import { SIGNUP_CITIES, useSignupForm } from 'modules/jogador/auth/useSignupForm';
import { AuthLayout } from 'modules/jogador/auth/components/AuthLayout';
import { PasswordInput } from 'modules/jogador/auth/components/PasswordInput';
import { OrDivider, SocialButtons } from 'modules/jogador/auth/components/SocialButtons';
import { Input } from 'shared/components/ui/Input';
import { Button } from 'shared/components/ui/Button';
import { ROUTES } from 'shared/constants/routes';
import { UserRole } from 'shared/types/user';
import { PlayerSportLevel } from 'modules/jogador/profile/content';
import { SPORT_CHOICES } from 'modules/arena/onboarding/content';
import { SPRING } from 'shared/components/motion/tokens';
import { cn } from 'shared/utils/cn';
import { AUTOPILOT_PARAM, DEMO_OWNER, pause, typeInto } from 'shared/demo/autopilot';
import { useAutopilot } from 'shared/demo/useAutopilot';
import { AutopilotStatus } from 'shared/demo/AutopilotStatus';

const ROLE_OPTIONS: { value: UserRole; title: string; description: string; icon: LucideIcon }[] = [
  { value: 'jogador', title: 'Sou jogador', description: 'Reservar quadras, entrar em rachas e dividir o Pix.', icon: Volleyball },
  { value: 'arena', title: 'Tenho uma arena', description: 'Agenda, cobranças e relatórios da minha arena.', icon: Store },
];

// Etapas mostradas no topo: o dono de arena segue para o cadastro da arena depois da conta.
const STEPS: Record<'jogador' | 'arena', string[]> = {
  jogador: ['Seus dados', 'Esportes'],
  arena: ['Seus dados', 'Sua arena', 'Painel pronto'],
};

const LEVEL_OPTIONS: PlayerSportLevel['level'][] = ['Iniciante', 'Intermediário', 'Avançado'];

function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="flex items-center gap-2" aria-label="Etapas do cadastro">
      {steps.map((label, index) => {
        const isDone = index < current;
        const isCurrent = index === current;
        return (
          <li key={label} className="flex flex-1 flex-col gap-1.5" aria-current={isCurrent ? 'step' : undefined}>
            <span className="h-1.5 overflow-hidden rounded-full bg-chip">
              <motion.span
                className="block h-full rounded-full bg-primary"
                initial={false}
                animate={{ width: isDone || isCurrent ? '100%' : '0%' }}
                transition={{ duration: 0.4 }}
              />
            </span>
            <span className={cn('text-xs font-medium', isCurrent ? 'text-ink' : 'text-ink-secondary')}>
              {index + 1}. {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="flex items-center gap-2 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
      <CircleAlert className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      {message}
    </p>
  );
}

export function SignupPage() {
  const form = useSignupForm();
  const audience = form.role === 'arena' ? 'arena' : 'jogador';
  const steps = STEPS[audience];

  const [importedFrom, setImportedFrom] = useState<string | null>(null);
  const autopilot = useAutopilot();
  const [searchParams] = useSearchParams();
  // Sempre a versão mais recente do formulário: o envio lê os campos do render atual.
  const formRef = useRef(form);
  formRef.current = form;

  function simulateOwnerSignup() {
    const f = () => formRef.current;
    autopilot.run(async (signal, report) => {
      report('Escolhendo o perfil de dono de arena');
      f().setRole('arena');
      await pause(700, signal);
      report('Digitando o nome');
      await typeInto((value) => f().setName(value), DEMO_OWNER.name, signal, 'name');
      report('Digitando o e-mail');
      await typeInto((value) => f().setEmail(value), DEMO_OWNER.email, signal, 'email', 18);
      report('Digitando o WhatsApp');
      await typeInto((value) => f().setPhone(value), DEMO_OWNER.phoneDigits, signal, 'phone', 60);
      report('Criando uma senha forte');
      await typeInto((value) => f().setPassword(value), DEMO_OWNER.password, signal, 'password', 35);
      await pause(300, signal);
      report('Aceitando os termos');
      f().setAcceptedTerms(true);
      await pause(700, signal);
      report('Criando a conta');
      await f().doRegister(true);
    });
  }

  // Link com ?auto=1 (login, roteiro) já abre a tela simulando — inclusive quando a pessoa
  // já está no cadastro e o parâmetro chega depois (a página não remonta).
  const autoRequested = searchParams.get(AUTOPILOT_PARAM) !== null;
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoRequested) {
      autoStarted.current = false;
      return;
    }
    if (!autoStarted.current) {
      autoStarted.current = true;
      simulateOwnerSignup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRequested]);

  // Cadastro social simulado: traz nome e e-mail "do provedor" para o visitante só conferir.
  // O aviso fica no próprio formulário (não em toast): um toast no canto cobriria o botão de enviar.
  function handleSocial(provider: 'google' | 'apple') {
    if (!form.name) form.setName(form.role === 'arena' ? 'Carlos Mendes' : 'Rafael Nunes');
    if (!form.email) form.setEmail(form.role === 'arena' ? 'carlos@minhaarena.com' : 'rafael@email.com');
    setImportedFrom(provider === 'google' ? 'Google' : 'Apple');
  }

  if (form.step === 'sports') {
    return (
      <AuthLayout audience="jogador">
        <Stepper steps={steps} current={1} />
        <h1 className="mt-8 font-heading text-3xl font-bold text-ink">Seu perfil esportivo</h1>
        <p className="mt-2 text-sm text-ink-secondary">
          Escolha o que você joga e seu nível. Usamos isso para indicar rachas e aulas na sua medida.
        </p>

        <form onSubmit={form.handleSportsSubmit} className="mt-8 flex flex-col gap-3">
          {SPORT_CHOICES.map((sport) => {
            const isSelected = form.selectedSports.includes(sport);
            return (
              <div
                key={sport}
                className={cn(
                  'rounded-xl border bg-surface transition-colors',
                  isSelected ? 'border-primary shadow-card' : 'border-border/70 hover:border-primary/40'
                )}
              >
                <button
                  type="button"
                  onClick={() => form.toggleSport(sport)}
                  aria-pressed={isSelected}
                  className="flex w-full items-center justify-between px-4 py-3 text-left"
                >
                  <span className="font-medium text-ink">{sport}</span>
                  <span
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full border transition-colors',
                      isSelected ? 'border-primary bg-primary text-surface' : 'border-border'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
                  </span>
                </button>
                {isSelected && (
                  <div className="flex gap-1.5 px-4 pb-3" role="radiogroup" aria-label={`Nível em ${sport}`}>
                    {LEVEL_OPTIONS.map((level) => {
                      const isActive = (form.levelsBySport[sport] ?? 'Iniciante') === level;
                      return (
                        <button
                          key={level}
                          type="button"
                          role="radio"
                          aria-checked={isActive}
                          onClick={() => form.setSportLevel(sport, level)}
                          className={cn(
                            'flex-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors',
                            isActive ? 'bg-primary text-surface' : 'bg-chip text-ink-secondary hover:bg-accent-soft'
                          )}
                        >
                          {level}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <FormError message={form.error} />

          <div className="mt-3 flex gap-3">
            <Button type="button" variant="ghost" onClick={form.backToInfo}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Voltar
            </Button>
            <Button
              type="submit"
              className="flex-1 py-2.5"
              disabled={form.isSubmitting || form.selectedSports.length === 0}
            >
              {form.isSubmitting ? 'Criando conta...' : 'Concluir cadastro'}
            </Button>
          </div>
          {form.selectedSports.length === 0 && (
            <p className="text-center text-xs text-ink-secondary">Escolha pelo menos um esporte.</p>
          )}
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout audience={audience}>
      <AutopilotStatus label={autopilot.label} onStop={autopilot.stop} />
      <Stepper steps={steps} current={0} />

      <h1 className="mt-8 font-heading text-[28px] font-bold leading-tight text-ink">Crie sua conta</h1>
      <p className="mt-2 text-sm text-ink-secondary">
        {form.role === 'arena'
          ? 'Primeiro seus dados. Depois você cadastra a arena em cerca de 3 minutos.'
          : 'Grátis para jogadores. Reserve, entre em rachas e divida o Pix.'}
      </p>

      <div
        className="mt-6 grid grid-cols-2 gap-1 rounded-xl bg-chip p-1"
        role="radiogroup"
        aria-label="Tipo de conta"
      >
        {ROLE_OPTIONS.map(({ value, title, icon: Icon }) => {
          const isSelected = form.role === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => form.setRole(value)}
              className={cn(
                'relative flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isSelected ? 'text-ink' : 'text-ink-secondary hover:text-ink'
              )}
            >
              {isSelected && (
                <motion.span
                  layoutId="signup-role-pill"
                  transition={SPRING}
                  className="absolute inset-0 rounded-lg bg-surface shadow-card"
                />
              )}
              <Icon className={cn('relative h-4 w-4', isSelected && 'text-primary')} aria-hidden="true" />
              <span className="relative">{title}</span>
            </button>
          );
        })}
      </div>

      <form onSubmit={form.handleInfoSubmit} className="mt-6 flex flex-col gap-4">
        {importedFrom && (
          <p role="status" className="flex items-start gap-2 rounded-lg bg-success-soft px-3 py-2 text-sm text-success">
            <CircleCheck className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>Nome e e-mail importados do {importedFrom}. Confira e complete o WhatsApp e a senha.</span>
          </p>
        )}
        <Input
          label="Nome completo"
          name="name"
          icon={UserRound}
          placeholder="Seu nome"
          autoComplete="name"
          className="py-2.5"
          value={form.name}
          onChange={(e) => form.setName(e.target.value)}
          required
        />
        <Input
          label="E-mail"
          name="email"
          type="email"
          icon={Mail}
          placeholder="voce@email.com"
          autoComplete="email"
          className="py-2.5"
          value={form.email}
          onChange={(e) => form.setEmail(e.target.value)}
          required
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="WhatsApp"
            name="phone"
            type="tel"
            inputMode="numeric"
            icon={Phone}
            placeholder="(85) 99999-9999"
            autoComplete="tel-national"
            className="py-2.5"
            value={form.phone}
            onChange={(e) => form.setPhone(e.target.value)}
            pattern="\(\d{2}\) \d{4,5}-\d{4}"
            title="Informe o celular com DDD"
            required
          />
          <label className="flex flex-col gap-1 text-sm text-ink">
            Cidade
            <span className="relative flex items-center">
              <MapPin className="pointer-events-none absolute left-3 h-4 w-4 text-ink-secondary" aria-hidden="true" />
              <select
                name="city"
                value={form.city}
                onChange={(e) => form.setCity(e.target.value)}
                className="w-full appearance-none rounded-lg border border-border bg-surface py-2.5 pl-9 pr-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {SIGNUP_CITIES.map((city) => (
                  <option key={city}>{city}</option>
                ))}
              </select>
            </span>
          </label>
        </div>
        <PasswordInput
          name="password"
          autoComplete="new-password"
          placeholder="Mínimo de 8 caracteres"
          minLength={8}
          showStrength
          className="py-2.5"
          value={form.password}
          onChange={(e) => form.setPassword(e.target.value)}
          required
        />

        <label className="flex cursor-pointer items-start gap-2.5 text-sm text-ink-secondary">
          <input
            type="checkbox"
            checked={form.acceptedTerms}
            onChange={(e) => form.setAcceptedTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-primary"
            required
          />
          <span>
            Concordo com os <span className="font-medium text-ink underline">Termos de uso</span> e a{' '}
            <span className="font-medium text-ink underline">Política de privacidade</span>.
          </span>
        </label>

        <FormError message={form.error} />

        <Button type="submit" className="w-full py-3" disabled={form.isSubmitting}>
          {form.isSubmitting ? 'Criando conta...' : form.role === 'arena' ? 'Criar conta e continuar' : 'Continuar'}
          {!form.isSubmitting && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
        </Button>
      </form>

      <div className="my-6">
        <OrDivider />
      </div>
      <SocialButtons onSelect={handleSocial} disabled={form.isSubmitting} />

      <button
        type="button"
        onClick={simulateOwnerSignup}
        disabled={autopilot.isRunning}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border py-2.5 text-sm text-ink-secondary transition-colors hover:border-primary hover:text-primary-dark disabled:opacity-60"
      >
        <WandSparkles className="h-4 w-4" aria-hidden="true" />
        <span>
          <span className="font-medium">Modo demonstração:</span> simular cadastro de dono de arena
        </span>
      </button>

      <p className="mt-8 text-center text-sm text-ink-secondary">
        Já tem conta?{' '}
        <Link to={ROUTES.LOGIN} className="font-semibold text-primary hover:underline">
          Entrar
        </Link>
      </p>
    </AuthLayout>
  );
}
